/**
 * Custom QR code renderer with branded styling.
 *
 * Draws QR codes with:
 * - Rounded dot modules
 * - Custom finder patterns (eyes) with rounded outer borders and circular inner dots
 * - Clean white background
 *
 * Uses the `qrcode` library's `create()` to get the raw module matrix,
 * then renders everything manually onto a canvas context.
 *
 * Ported from treasures.to.
 */

import QRCode from "qrcode";

// Default foreground color (forest green).
const DEFAULT_FOREGROUND = "#22864e";

/**
 * Check if a module at (row, col) is part of a finder pattern (the three 7x7 corners).
 */
function isFinderModule(row: number, col: number, size: number): boolean {
  if (row <= 6 && col <= 6) return true;
  if (row <= 6 && col >= size - 7) return true;
  if (row >= size - 7 && col <= 6) return true;
  return false;
}

/**
 * Draw a single rounded-corner rectangle on the canvas.
 */
export function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
) {
  r = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

/**
 * Draw a custom finder pattern (eye) at the given origin.
 */
function drawFinderPattern(
  ctx: CanvasRenderingContext2D,
  originX: number,
  originY: number,
  moduleSize: number,
  darkColor: string,
) {
  const outerSize = 7 * moduleSize;
  const outerRadius = moduleSize * 1.4;

  ctx.fillStyle = darkColor;
  roundRect(ctx, originX, originY, outerSize, outerSize, outerRadius);
  ctx.fill();

  const gapOffset = moduleSize;
  const gapSize = 5 * moduleSize;
  const gapRadius = moduleSize * 1.0;

  ctx.fillStyle = "#FFFFFF";
  roundRect(ctx, originX + gapOffset, originY + gapOffset, gapSize, gapSize, gapRadius);
  ctx.fill();

  const innerOffset = 2 * moduleSize;
  const innerSize = 3 * moduleSize;
  const innerRadius = moduleSize * 0.8;

  ctx.fillStyle = darkColor;
  roundRect(ctx, originX + innerOffset, originY + innerOffset, innerSize, innerSize, innerRadius);
  ctx.fill();
}

export interface StyledQROptions {
  /** Text to encode */
  text: string;
  /** Pixel width/height of the resulting QR (excluding margin) */
  size: number;
  /** Quiet-zone modules around the code (default 1) */
  margin?: number;
  /** Error correction level (default 'H' for logo overlay compatibility) */
  errorCorrectionLevel?: "L" | "M" | "Q" | "H";
  /** Override the foreground color for both data dots and finder patterns. */
  foregroundColor?: string;
}

/**
 * Render a styled QR code directly onto a canvas 2D context.
 */
export function drawStyledQR(
  ctx: CanvasRenderingContext2D,
  destX: number,
  destY: number,
  options: StyledQROptions,
) {
  const {
    text,
    size,
    margin = 1,
    errorCorrectionLevel = "H",
    foregroundColor = DEFAULT_FOREGROUND,
  } = options;

  const qr = QRCode.create(text, { errorCorrectionLevel });
  const modules = qr.modules;
  const moduleCount = modules.size;
  const totalModules = moduleCount + margin * 2;
  const moduleSize = size / totalModules;

  const bgRadius = size * 0.04;
  ctx.fillStyle = "#FFFFFF";
  roundRect(ctx, destX, destY, size, size, bgRadius);
  ctx.fill();

  const dotScale = 0.88;
  const dotRadius = (moduleSize * dotScale) / 2;

  ctx.fillStyle = foregroundColor;
  for (let row = 0; row < moduleCount; row++) {
    for (let col = 0; col < moduleCount; col++) {
      if (isFinderModule(row, col, moduleCount)) continue;

      const isDark = modules.data[row * moduleCount + col] === 1;
      if (!isDark) continue;

      const cx = destX + (col + margin + 0.5) * moduleSize;
      const cy = destY + (row + margin + 0.5) * moduleSize;

      ctx.beginPath();
      ctx.arc(cx, cy, dotRadius, 0, 2 * Math.PI);
      ctx.fill();
    }
  }

  const finderColor = foregroundColor;

  drawFinderPattern(
    ctx,
    destX + margin * moduleSize,
    destY + margin * moduleSize,
    moduleSize,
    finderColor,
  );
  drawFinderPattern(
    ctx,
    destX + (margin + moduleCount - 7) * moduleSize,
    destY + margin * moduleSize,
    moduleSize,
    finderColor,
  );
  drawFinderPattern(
    ctx,
    destX + margin * moduleSize,
    destY + (margin + moduleCount - 7) * moduleSize,
    moduleSize,
    finderColor,
  );
}

/**
 * Convenience: render a styled QR code to a data-URL PNG string.
 */
export function styledQRToDataURL(
  text: string,
  size: number,
  errorCorrectionLevel: "L" | "M" | "Q" | "H" = "H",
  foregroundColor?: string,
): string {
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Could not get canvas context");

  drawStyledQR(ctx, 0, 0, {
    text,
    size,
    margin: 1,
    errorCorrectionLevel,
    foregroundColor,
  });

  return canvas.toDataURL("image/png", 1.0);
}
